import React, { useState, useEffect } from "react";
import axios from "axios";
import { SBChatShow, SBChatChannel } from "../../../messaging/stores/SBChatStore";

const SBSendMessage: React.FC<{ profileId: string; variant?: 'button' | 'link' | 'chip'; chipClass?: string }> = ({ profileId, variant = 'button', chipClass = 'btn btn-sm btn-outline-primary' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  useEffect(() => {
    const token = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]',
    )?.content;
    setCsrfToken(token || null);
  }, []);

  const handleCreateChat = async () => {
    if (!csrfToken) {
      setError("CSRF token not found. Please refresh the page.");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `/profiles/${profileId}/create_chat.json`,
        {},
        {
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        },
      );
      SBChatShow.set(true);
      SBChatChannel.set(response.data.channel_url);
    } catch (error) {
      console.error("Error creating chat:", error);
      setError("Failed to create chat. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Styling for button variant (default)
  const buttonStyle: React.CSSProperties = {
    pointerEvents: isLoading ? "none" : "auto",
    cursor: isLoading ? "default" : "pointer",
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    textDecoration: 'none',
    background: 'white',
    color: '#085ec8',
    border: '2px solid #085ec8',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 6px rgba(8, 94, 200, 0.12)'
  };

  // Styling for link variant (for dropdown menus)
  const linkStyle: React.CSSProperties = {
    pointerEvents: isLoading ? "none" : "auto",
    cursor: isLoading ? "default" : "pointer",
    display: 'inline',
    color: '#333',
    textDecoration: 'none',
    fontSize: '0.875rem',
    transition: 'color 0.2s ease'
  };

  const handleMouseOver = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isLoading) return;

    if (variant === 'button') {
      e.currentTarget.style.background = '#085ec8';
      e.currentTarget.style.color = 'white';
      e.currentTarget.style.transform = 'translateY(-1px)';
      e.currentTarget.style.boxShadow = '0 4px 10px rgba(8, 94, 200, 0.2)';
    } else {
      e.currentTarget.style.color = '#085ec8';
    }
  };

  const handleMouseOut = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (variant === 'button') {
      e.currentTarget.style.background = 'white';
      e.currentTarget.style.color = '#085ec8';
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 6px rgba(8, 94, 200, 0.12)';
    } else {
      e.currentTarget.style.color = '#333';
    }
  };

  // Chip variant: visually identical to the mobile card's action chips
  // (btn btn-sm btn-outline-primary) so the action row reads as one system.
  if (variant === 'chip') {
    return (
      <div style={{ display: 'contents' }}>
        <a
          className={chipClass}
          onClick={handleCreateChat}
          style={{ pointerEvents: isLoading ? 'none' : 'auto', cursor: 'pointer' }}
        >
          <i className={`fa ${isLoading ? 'fa-spinner fa-spin' : 'fa-envelope'} me-1`} aria-hidden="true"></i>
          {isLoading ? 'Opening…' : 'Message'}
        </a>
      </div>
    );
  }

  return (
    <div style={{ display: variant === 'link' ? 'inline' : 'block' }}>
      <a
        className={variant === 'button' ? "popup-with-zoom-anim col-12" : ""}
        onClick={handleCreateChat}
        style={variant === 'button' ? buttonStyle : linkStyle}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
      >
        {variant === 'link' ? (
          <>
            <i className="fa fa-envelope me-2" style={{ width: '16px', color: '#085ec8' }} aria-hidden="true"></i>
            {isLoading ? "Creating Chat..." : "Send Message"}
          </>
        ) : (
          <>
            <i className="fa fa-envelope" aria-hidden="true"></i>
            {isLoading ? "Creating Chat..." : "Send Message"}
          </>
        )}
      </a>
      {error && <p style={{ color: "red", fontSize: '0.875rem', marginTop: '0.5rem' }}>{error}</p>}
    </div>
  );
};

export default SBSendMessage;
